import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import { addSeconds, format, isToday, isTomorrow } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

function OrderTimeCard({ color }) {
  const location = useLocation();
  const isCheckoutPage = location.pathname.includes('checkout');

  const { checkoutData, type, trackOrderData, restaurantDetails } = useSelector(
    ({ order, profile, restaurant, time: selectedTime }) => ({
      checkoutData: order.checkoutData,
      type: order?.type,
      time: selectedTime.selectedTime,
      trackOrderData: profile.trackingOrderResponse?.response,
      restaurantDetails: restaurant.selectedRestaurantData,
    })
  );

  const isDelivery = type.value === 'delivery';

  const getFulfilledTimestamp = () => {
    if (isCheckoutPage && checkoutData) {
      return isDelivery
        ? checkoutData?.schedule?.delivery
        : checkoutData?.schedule?.takeout;
    }
    if (trackOrderData && trackOrderData?.progress?.fulfilled?.scheduled) {
      return trackOrderData.progress.fulfilled.scheduled;
    }
    return null;
  };

  const fulfilledTimeStamp = getFulfilledTimestamp();

  const fulfilledStart = fulfilledTimeStamp
    ? new Date(fulfilledTimeStamp * 1000)
    : new Date();

  const windowSeconds =
    restaurantDetails?.windows[type.value] || (isDelivery ? 15 * 60 : 5 * 60);
  const fulfilledEnd = addSeconds(fulfilledStart, windowSeconds);

  const getDateLabel = (date) => {
    const zonedDate = utcToZonedTime(date, restaurantDetails.timezone);

    if (isToday(zonedDate)) {
      return 'TODAY';
    }
    if (isTomorrow(zonedDate)) {
      return 'TOMORROW';
    }

    return format(zonedDate, 'MMM d, yyyy');
  };

  const fulfilledStartTime = format(
    utcToZonedTime(fulfilledStart, restaurantDetails.timezone),
    'hh:mmaaa'
  );
  const fulfilledEndTime = format(
    utcToZonedTime(fulfilledEnd, restaurantDetails.timezone),
    'hh:mmaaa'
  );

  const dateLabel = getDateLabel(fulfilledStart);
  const dateSuffix =
    dateLabel === 'TODAY' || dateLabel === 'TOMORROW' ? '' : `, ${dateLabel}`;

  if (!checkoutData) {
    return <Typography />;
  }

  return (
    <Typography component="p" color={color}>
      {isDelivery
        ? `Estimated Dropoff: ${
            !dateSuffix ? dateLabel : ''
          } ${fulfilledStartTime}-${fulfilledEndTime}${dateSuffix}`
        : `Estimated Pickup: ${
            !dateSuffix ? dateLabel : ''
          } ${fulfilledStartTime}-${fulfilledEndTime}${dateSuffix}`}
    </Typography>
  );
}

OrderTimeCard.propTypes = {
  color: PropTypes.string,
};

OrderTimeCard.defaultProps = {
  color: 'primary',
};

export default OrderTimeCard;
